<template>
  <section class="item-flaw-summary" aria-label="Items with flaws inline">
    <!-- PAGE TITLE (same key used by ItemSummaryList) -->
    <p class="list-title">{{ $t('itemsummarylist.title') }}</p>

    <div
      v-for="(info, iIdx) in infos"
      :key="'info-' + iIdx"
      class="info-group"
    >
      <!-- GROUP HEADER: Building - Floor (fallbacks provided) -->
      <div class="section-title">
        {{
          (info.buildingName && info.floorName)
            ? `${info.buildingName} - ${info.floorName}`
            : (info.buildingName || info.name || info.title || $t('summary.section_items'))
        }}
      </div>

      <article
        v-for="(item, idx) in info.roundHistoryItems"
        :key="'item-' + iIdx + '-' + (item.id || idx)"
        class="item-card"
        :class="statusClass(item)"
      >
        <header class="item-header">
          <h3
            class="item-title"
            :class="{ 'has-flaws': hasFlaws(item) }"
          >
           <span
              v-if="positionNumber(item)"
              class="item-position"
            >
              {{ positionNumber(item) }}.
            </span>

            {{ preferCategoryLabel(item) }}
            <span
              v-if="hasFlaws(item)"
              class="flaw-icon"
              role="img"
              :aria-label="$t('summary.label_flaws')"
              title="Has flaws"
            />
          </h3>
          <span class="badge" :class="statusClass(item)">
            {{ statusLabel(item) }}
          </span>
        </header>

        <!-- Inline flaws for this item (only the ones relevant to this round) -->
        <ul v-if="filteredFlaws(item).length" class="flaw-list">
          <li v-for="(flaw, fIdx) in filteredFlaws(item)" :key="'flaw-' + fIdx" class="flaw">
            <div class="flaw-line">
              <span class="flaw-title">{{ flaw.title || flaw.name || $t('summary.flaw') }}</span>
              <span class="flaw-meta">
                {{ moment(flaw.created).format('YYYY-MM-DD') }}
              </span>
            </div>
            <p v-if="flaw.description" class="flaw-desc">
              {{ flaw.description }}
            </p>
          </li>
        </ul>

        <!-- SERVICE INFORMATION (read-only, driven by the same logic as the modal) -->
        <section
          v-if="visibleFieldsFor(item).length"
          class="service-info"
          aria-label="Service information"
        >
          <div class="service-title">{{ $t('flaw_modal.service_information') }}</div>

          <div
            v-for="(pair, pIdx) in chunk(visibleFieldsFor(item), 2)"
            :key="'svc-row-' + pIdx"
            class="svc-row"
          >
            <div v-for="field in pair" :key="field" class="svc-col">
              <div class="svc-label">
                {{ labelFor(field) }}
              </div>
              <div class="svc-value">
                <!-- keep your template-based number hint -->
                <template v-if="field.includes('Year') || field === 'FusibleLinkDegrees'">
                  {{ Number(valueFor(item, field)) || valueFor(item, field) }}
                </template>
                <template v-else>
                  {{ valueFor(item, field) }}
                </template>
              </div>
            </div>
          </div>
        </section>
      </article>

      <div class="page-break"></div>
    </div>
  </section>
</template>

<script>
import moment from 'moment'
import fireEquipmentService from '@/mixins/fireEquipmentService.js'
import iconMixin from '@/mixins/iconMixin.js'

export default {
  name: 'ItemFlawSummary',
  mixins: [iconMixin],
  props: {
    infos: { type: Array, required: true },
    round: { type: Object, required: true }
  },
  data() {
    return {
      fieldLabels: fireEquipmentService.getFieldLabels(),
      fieldMap: fireEquipmentService.getFieldMap() || {},
      numberFieldsLc: new Set([
        "chargeYear","revisionYear","installationYear","testPressureYear",
        "BatteryInstallationYear","BatteryRevisionYear",
        "ElectrodesInstallationYear","ElectrodesRevisionYear",
        "fusibleLinkDegrees","gasspringRevisionYear",
        "batteryRevisionYear","batteryInstallationYear","electrodesRevisionYear",
        "electrodesInstallationYear","fusibleLinkYear","gasspringRevisionYear",
        "RevisionYear","ChargeYear","InstallationYear","TestPressureYear",
        "FusibleLinkYear","FusibleLinkDegrees","GasspringRevisionYear"
      ].map(s => s.toLowerCase()))
    }
  },
  methods: {
    moment,

    positionNumber(item) {
      const extra = this.extraOf(item)

      return (
        item?.positionNumber ||
        item?.position ||
        item?.mapItem?.positionNumber ||
        item?.mapItem?.position ||
        this.ciGet(extra, 'positionNumber') ||
        this.ciGet(extra, 'position')
      )
    },

    isNumericField(fieldKey) {
      return this.numberFieldsLc.has(String(fieldKey).toLowerCase())
    },

    ciGet(obj, key) {
      if (!obj) return undefined
      if (Object.prototype.hasOwnProperty.call(obj, key)) return obj[key]

      const lc = String(key).toLowerCase()
      for (const k in obj) {
        if (String(k).toLowerCase() === lc) return obj[k]
      }
      return undefined
    },

    labelFor(fieldKey) {
      const tKey = this.fieldLabels[fieldKey]
      return tKey ? this.$t(tKey) : fieldKey
    },

    extraOf(item) {
      const candidates = [
        item?.roundHistoryMapItemExtra,
        item?.mapItemExtra,
        item?.mapItem?.mapItemExtra,
        item?.mapItem?.roundHistoryMapItemExtra,
        item?.extra,
        item?.mapItem?.extra
      ].filter(Boolean)

      return Object.assign({}, ...candidates)
    },

    valueFor(item, fieldKey) {
      const extra = this.extraOf(item)
      const mapped = this.fieldMap?.[fieldKey] 

      let val = this.ciGet(extra, fieldKey)

    
      if ((val === undefined || val === null || val === '') && mapped) {
        val = this.ciGet(extra, mapped)
      }

     
      if (val === undefined || val === null || val === '') return '–'

     
      if (this.isNumericField(fieldKey)) {
        const n = Number(val)
        return Number.isNaN(n) ? val : n
      }

      return val
    },

    toMs(d) {
      if (!d) return null
      const t = +new Date(d)
      return Number.isNaN(t) ? null : t
    },

    filteredFlaws(item) {
      if (!item || !item.flaws || !item.flaws.length) return []

      const start = this.toMs(this.round?.created)
      const end   = this.toMs(this.round?.done)

      return item.flaws.filter(f => {
        if (item?.continueWithoutAdding && !end && !f?.done) return true

        const t = this.toMs(f?.created)
        if (t == null) return true

        if (start != null && t <= start) return false
        if (end   != null && t >= end)   return false

        return true
      })
    },

    hasFlaws(item) {
      return this.filteredFlaws(item).length > 0
    },

    statusLabel(item) {
      const flaws = this.filteredFlaws(item)
      if (!item?.controlled && !item?.continueWithoutAdding) {
        return this.$t('summary.label_unchecked')
      }
      if (flaws.length > 0) {
        return this.$t('summary.label_flaws')
      }
      return this.$t('summary.label_ok')
    },

    statusClass(item) {
      const label = this.statusLabel(item)
      if (label === this.$t('summary.label_flaws')) return 'is-flaw'
      if (label === this.$t('summary.label_ok')) return 'is-ok'
      return 'is-unchecked'
    },

    inventoryTypeOf(item) {
      return item?.inventoryType
        || item?.fireProtection?.inventoryType
        || item?.mapItem?.fireProtection?.inventoryType
        || 'misc'
    },
    visibleFieldsFor(item) {
      const type = this.inventoryTypeOf(item)
      return fireEquipmentService.getVisibleFields(type) || []
    },

    chunk(arr, size = 2) {
      const out = []
      for (let i = 0; i < arr.length; i += size) {
        out.push(arr.slice(i, i + size))
      }
      return out
    }
  }
}
</script>

<style lang="scss" scoped>
.item-flaw-summary { margin-top: 1.5rem; }

/* Page title, matching your ItemSummaryList look */
.list-title {
  font-size: 1.5rem;
  margin: 0 0 0.75rem;
  font-weight: 600;
}

/* Group header: "Building - Floor" */
.section-title {
  font-size: 1.3rem;
  margin: 1rem 0 0.8rem;
  font-weight: 600;
  page-break-inside: avoid;
}

.item-card {
  border: 1px solid #ddd;
  border-left-width: 6px;
  padding: 0.75rem 1rem;
  border-radius: 4px;
  margin-bottom: 0.75rem;
  background: #fff;
  page-break-inside: avoid;

  &.is-ok { border-left-color: #7bca78; }
  &.is-flaw { border-left-color: #da6060; }
  &.is-unchecked { border-left-color: #d3d3d3; }
}

.item-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.25rem;
}

.item-title {
  font-weight: 600;
  font-size: 1rem;
  margin: 0;
  display: inline-flex;
  align-items: center;
  gap: .4rem;
}

.badge {
  font-size: 0.75rem;
  padding: 0.2rem 0.5rem;
  border-radius: 3px;
  border: 1px solid #ccc;

  &.is-ok { background: #f3fbf3; }
  &.is-flaw { background: #fff3f3; }
  &.is-unchecked { background: #f8f8f8; }
}

.flaw-list {
  list-style: none;
  padding-left: 0;
  margin: 0.25rem 0 0;
}

.flaw {
  border-top: 1px dashed #e6e6e6;
  padding-top: 0.5rem;
  margin-top: 0.5rem;
  background-image: none;
}

.flaw-icon {
  display: inline-block;
  width: 1.1rem;
  height: 1.1rem;
  background-image: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGQAAABkCAIAAAD/gAIDAAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAAf5JREFUeNrs3b9qwlAUx/FezRDBRWhWB9fqKLi6iIuOBWcfQHwT8xAufYkOWYybQ3Vz0qWCimKw/ktTlEKLRoVKT+L3h2BQh/DhnuO9N2JUoVDI5/Ou6z6Q01FKtdttrVgs1ut1OM6m2Wxqy+XSO/oYj8edjopEQPkVd7dLpNO6YTiOo+1f8qSsahWao8k1Gsly2Ts4DCXGlF/Dikb3BxhdEbDAAgsssMAiYIF1m2hCzuOpVvMePh8Y2fZrpcLIogzBAouABRZYYIEFFgELLLDAAgssEjSsxXAI1qVxBgOw/izvrRZY9KzrM+l2wbo069kMrD/LyLbBomcFduyEZGStBDS1wGBNBXxdCsJaiJ/EgxW6MlzP52AFqWHJwpr2epRheFY8wShDIb0frGBiSdjeYyF9b2Uo43KGLCznBIpDzwrcioeeFTqsCcudy9eAQib3srBWslc89KxwYcnZjZCFdfQCj5zdCMoQrLudwcvZjWC5QxmCBdb5WZWcH4yIw5J8jUeTdkL//ucN9CywwAILLLAIWKGbZz33+z7vvpmm92BkUYZggUXAAgsssJiU3jQvqRQjizIEi4AFFlhggXXPWO52i8WpfOMcZvCJTCZnml+3V1MKnR9Sm81jNnvA0nXde9INI1kqQeOTWCymWZYVj8e5zah/lFIe1KcAAwC7kqFyu33jVAAAAABJRU5ErkJggg==);
  background-repeat: no-repeat;
  background-position: center;
  background-size: 1em 1em;
}

.flaw-line {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
}

.flaw-title { font-weight: 600; }

.flaw-meta {
  font-size: 0.75rem;
  opacity: 0.8;
}

.flaw-desc { margin: 0.25rem 0 0; }

/* --- Service information styles --- */
.service-info {
  margin-top: 0.6rem;
  border-top: 1px dashed #e6e6e6;
  padding-top: 0.6rem;

  .service-title {
    font-weight: 600;
    margin-bottom: 0.4rem;
  }

  .svc-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: .6rem 1rem;
    margin-bottom: .4rem;
  }

  .svc-col { display: grid; grid-template-rows: auto auto; }

  .svc-label {
    font-size: .85rem;
    opacity: .85;
  }

  .svc-value {
    font-weight: 600;
    word-break: break-word;
  }
}

.no-flaws {
  margin: 0.25rem 0 0;
  &.unchecked { opacity: 0.75; font-style: italic; }
}

.page-break { height: 1px; }

@media print {
  .item-card { break-inside: avoid; }
  .section-title { break-after: avoid; }
  .service-info { break-inside: avoid; }
  .page-break { break-after: page; }
}
</style>
